/**
 * Focus Trapping Class.
 * Used to trap focus when a modal is open.
 */
class FocusTrapper {
	modalElems = [];

	/**
	 * Target modal.
	 *
	 * @type {HTMLElement}
	 */
	modal = null;

	/**
	 * An array of selectors that appear outside and above the modal,
	 * ie. global menu btn or home link.
	 *
	 * @type {string[]}
	 */
	globalStart = [];

	/**
	 * Track offset for focus start,
	 * when global start items are available.
	 *
	 * @type {number}
	 */
	globalStartCount = 0;

	/**
	 * An array of selectors that appear outside and after the modal,
	 * ie. an obtrusive CTA link.
	 *
	 * @type {string[]}
	 */
	globalEnd = [];

	/**
	 * Event listener func.
	 * 
	 * @type {Function}
	 */
	trappingEvent = null;

	/**
	 * Pass entry point for the class.
	 *
	 * @param {HTMLElement} modal The modal to trap focus within.
	 * @param {string[]} globalStart An array of selectors that appear outside and above the modal, ie. menuBtn and logoLink.
	 * @param {string[]} globalEnd An array of selectors that appear outside and after the modal, ie. ctaLink.
	 */
	constructor(modal, globalStart = [], globalEnd = []) {
		this.modal = modal;
		this.globalStart = globalStart;
		this.globalEnd = globalEnd;
		this.getModalFocusElems();
	}

	/**
	 * Gather all of the focusable elems in the modal.
	 * Store them in a global array.
	 */
	getModalFocusElems() {
		// Gather all actively focusable elems within the modal.
		let tmp = this.modal.querySelectorAll(
			'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="search"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
		);
		tmp = Array.prototype.slice.call(tmp);

		// Insert in global elems outside modal that appear above. ie menuBtn and logoLink.
		if (Array.isArray(this.globalStart) && this.globalStart.length) {
			// Slice creates a shallow copy, then we reverse it.
			this.globalStart
				.slice()
				.reverse()
				.forEach((i) => {
					tmp.unshift(document.querySelector(i));
				});

			// Used in trap focus func below.
			this.globalStartCount = this.globalStart.length;
		}

		// Add in global elems outside modal that appear below. ie ctaLink.
		if (Array.isArray(this.globalEnd) && this.globalEnd.length) {
			tmp = [
				...tmp,
				...this.globalEnd.map((i) => document.querySelector(i))
			];
		}
		this.modalElems = tmp;
	}

	/**
	 * Trap focus within the modal.
	 * Define users pathways within this method.
	 *
	 * @param {Object} e The event object.
	 */
	handleTrapping(e) {
		const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

		if (! isTabPressed) {
			return;
		}

		const firstFocusableEl = this.modalElems[0];
		
		/**
		 * Ensure last focusable elem can be focused.
		 * Important if an accordion is the last set of items.
		 */
		let lastFocusableEl;
		for (let i = this.modalElems.length - 1; i > 0; i--) {
			if (this.modalElems[i].getAttribute('tabindex') !== '-1') {
				lastFocusableEl = this.modalElems[i];
				break;
			}
		}

		if (e.shiftKey) {
			/* shift + tab */

			/**
			 * Basic loop from last item -> first.
			 */
			if (document.activeElement === firstFocusableEl) {
				e.preventDefault();
				lastFocusableEl.focus();
			}

			/**
			 * If there are global start items,
			 * on `shift + tab` we want to go from modal -> last global start item.
			 */
			if (
				this.globalStartCount &&
				document.activeElement === this.modalElems[this.globalStartCount]
			) {
				e.preventDefault();
				this.modalElems[this.globalStartCount - 1].focus();
			}

			/* tab */
			/**
			 * Last item -> first item loop.
			 */
		} else if (document.activeElement === lastFocusableEl) {
			e.preventDefault();
			firstFocusableEl.focus();

			/**
			 * Last of the global start items -> first inner modal item.
			 * ie. menuBtn -> modal.
			 */
		} else if (
			this.globalStartCount &&
			document.activeElement === this.modalElems[this.globalStartCount - 1]
		) {
			e.preventDefault();
			this.modalElems[this.globalStartCount].focus();
		}
	}

	/**
	 * Bind trap focus to the modal.
	 */
	bind() {
		this.trappingEvent = this.handleTrapping.bind(this);
		document.addEventListener('keydown', this.trappingEvent);
	}

	/**
	 * Unbind trap focus from the modal.
	 */
	unbind() {
		document.removeEventListener('keydown', this.trappingEvent);
	}
}

export default FocusTrapper;
